import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';

export interface VehiclePreventiveMaintenanceHistoryItem {
  vehicleId: number;
  preventiveMaintenanceId: number;
  checklistId: number;
  targetMileage: number | null;
  executedMileage: number | null;
  executedDate: string | null;
  nextExecutionMileage: number | null;
  nextExecutionDate: string | null;
  status: string;
  observations: string | null;
  vehiclePlate: string;
  preventiveMaintenanceName: string;
  id: number;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
}

export interface VehiclePreventiveMaintenanceHistorySummary {
  vehicleId: number;
  totalPreventivesDone: number;
  history: VehiclePreventiveMaintenanceHistoryItem[];
}

export interface VehiclePreventiveMaintenanceHistoryPaginatedResponse {
  items: VehiclePreventiveMaintenanceHistoryItem[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface VehiclePreventiveMaintenanceHistoryFilters {
  vehicleId?: number | null;
  plateNumber?: string | null;
  preventiveMaintenanceId?: number | null;
}

interface VehiclePreventiveMaintenanceHistoryApiResponse {
  success: boolean;
  message: string;
  data: {
    vehicleId: number;
    totalPreventivesDone: number;
    history: any[];
  } | null;
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class VehiclePreventiveMaintenanceHistoryService {
  private readonly baseUrl = `${environment.microservices.operation}/vehicle-preventive-maintenance-history`;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  getSummaryByVehicle(vehicleId: number): Observable<VehiclePreventiveMaintenanceHistorySummary> {
    return this.http
      .get<VehiclePreventiveMaintenanceHistoryApiResponse>(
        `${this.baseUrl}/vehicle/${vehicleId}/summary`
      )
      .pipe(
        map((response) => {
          const data = response?.data;

          const history: VehiclePreventiveMaintenanceHistoryItem[] = Array.isArray(
            data?.history
          )
            ? data!.history.map((item: any): VehiclePreventiveMaintenanceHistoryItem => ({
                vehicleId: item?.VehicleId ?? vehicleId,
                preventiveMaintenanceId: item?.PreventiveMaintenanceId ?? 0,
                checklistId: item?.ChecklistId ?? 0,
                targetMileage: item?.TargetMileage ?? null,
                executedMileage: item?.ExecutedMileage ?? null,
                executedDate: item?.ExecutedDate ?? null,
                nextExecutionMileage: item?.NextExecutionMileage ?? null,
                nextExecutionDate: item?.NextExecutionDate ?? null,
                status: item?.Status ?? 'Unknown',
                observations: item?.Observations ?? null,
                vehiclePlate: item?.VehiclePlate ?? '',
                preventiveMaintenanceName: item?.PreventiveMaintenanceName ?? '',
                id: item?.Id ?? 0,
                createdAt: item?.CreatedAt ?? null,
                updatedAt: item?.UpdatedAt ?? null,
                createdBy: item?.CreatedBy ?? null,
                updatedBy: item?.UpdatedBy ?? null
              }))
            : [];

          return {
            vehicleId: data?.vehicleId ?? vehicleId,
            totalPreventivesDone: data?.totalPreventivesDone ?? history.length,
            history
          };
        }),
        catchError((err) => {
          this.errorService.handleHttpError(
            err,
            'Erro ao carregar histórico de preventivas do veículo'
          );
          return throwError(() => err);
        })
      );
  }

  getAllPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters?: VehiclePreventiveMaintenanceHistoryFilters
  ): Observable<VehiclePreventiveMaintenanceHistoryPaginatedResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (filters) {
      if (filters.vehicleId != null) {
        params = params.set('vehicleId', String(filters.vehicleId));
      }
      if (filters.plateNumber) {
        params = params.set('plateNumber', filters.plateNumber);
      }
      if (filters.preventiveMaintenanceId != null) {
        params = params.set('preventiveMaintenanceId', String(filters.preventiveMaintenanceId));
      }
    }

    return this.http.get<any>(`${this.baseUrl}/list`, { params }).pipe(
      map((response: any) => {
        const rawItems = Array.isArray(response?.data) ? response.data : [];

        const items: VehiclePreventiveMaintenanceHistoryItem[] = rawItems.map((item: any) => ({
          vehicleId: item?.VehicleId ?? 0,
          preventiveMaintenanceId: item?.PreventiveMaintenanceId ?? 0,
          checklistId: item?.ChecklistId ?? 0,
          targetMileage: item?.TargetMileage ?? null,
          executedMileage: item?.ExecutedMileage ?? null,
          executedDate: item?.ExecutedDate ?? null,
          nextExecutionMileage: item?.NextExecutionMileage ?? null,
          nextExecutionDate: item?.NextExecutionDate ?? null,
          status: item?.Status ?? 'Unknown',
          observations: item?.Observations ?? null,
          vehiclePlate: item?.VehiclePlate ?? '',
          preventiveMaintenanceName: item?.PreventiveMaintenanceName ?? '',
          id: item?.Id ?? 0,
          createdAt: item?.CreatedAt ?? null,
          updatedAt: item?.UpdatedAt ?? null,
          createdBy: item?.CreatedBy ?? null,
          updatedBy: item?.UpdatedBy ?? null
        }));

        return {
          items,
          page: response?.pagination?.page ?? page,
          pageSize: response?.pagination?.pageSize ?? pageSize,
          totalItems: response?.pagination?.total ?? items.length,
          totalPages: response?.pagination?.totalPages ?? 1
        };
      }),
      catchError((err) => {
        this.errorService.handleHttpError(
          err,
          'Erro ao carregar histórico de preventivas'
        );
        return throwError(() => err);
      })
    );
  }
}

